import { request } from '@/common/services/request';

const RESOURCE_FIELDS = {
  'ip-kernels': ['ipKernel', 'ipKernelSnapshot'],
  'seed-plans': ['title', 'keyword', 'description', 'plannedAt'],
  'hot-pots': [
    'title',
    'hot',
    'url',
    'keyword',
    'description',
    'relevance',
    'status',
    'platforms',
    'mergedUrls',
  ],
  'fact-anchors': ['seedId', 'seedType', 'factAnchor'],
  'topic-lens': [
    'seedId',
    'seedType',
    'title',
    'audience',
    'coreQuestion',
    'tension',
    'thesis',
    'value',
    'angle',
    'keyPoints',
    'methodPath',
    'habitPath',
    'closingIntent',
    'recommendedArcType',
    'recommendedArcReason',
    'status',
    'reason',
    'score',
  ],
  'article-arcs': ['topicId', 'title', 'article', 'images'],
  'script-arcs': ['topicId', 'title', 'script'],
};

const RESOURCE_FILTER_FIELDS = {
  'fact-anchors': ['seedType'],
  'hot-pots': ['status'],
  'topic-lens': ['status'],
};

const RESOURCE_SEARCH_FIELDS = {
  'ip-kernels': ['ipKernel'],
  'seed-plans': ['title', 'keyword', 'description'],
  'hot-pots': ['title', 'keyword', 'platforms'],
  'fact-anchors': [],
  'topic-lens': ['title'],
  'article-arcs': ['title'],
  'script-arcs': ['title'],
};

const getPayloadData = (payload) => payload?.data || payload || {};

const getResourceUrl = (resource) => `/api/ip-creator/${resource}`;

const getResourceData = (resource, input) =>
  RESOURCE_FIELDS[resource].reduce(
    (data, field) => ({
      ...data,
      [field]: input[field],
    }),
    {},
  );

const getExactFilters = (resource, params) =>
  (RESOURCE_FILTER_FIELDS[resource] || []).reduce(
    (filters, field) => ({
      ...filters,
      ...(params[field] ? { [field]: params[field] } : {}),
    }),
    {},
  );

const getFieldSearches = (resource, params) =>
  RESOURCE_SEARCH_FIELDS[resource].reduce(
    (filters, field) => ({
      ...filters,
      ...(params[field] ? { [field]: params[field] } : {}),
    }),
    {},
  );

const getQueryParams = (resource, params) => {
  const queryParams = params || {};
  return Object.fromEntries(
    Object.entries({
      organizationId: queryParams.organizationId,
      ...getFieldSearches(resource, queryParams),
      ...getExactFilters(resource, queryParams),
      current: queryParams.current,
      page: queryParams.page,
      pageSize: queryParams.pageSize,
    }).filter(([, value]) => value !== undefined),
  );
};

const toProTableResult = (result) => ({
  data: result.data || [],
  success: true,
  total: result.meta?.total || 0,
});

export async function queryIpCreatorContents(params) {
  const result = await request('/api/ip-creator/contents', {
    method: 'GET',
    params: {
      organizationId: params?.organizationId,
    },
  });
  return result.data || {};
}

export async function queryIpCreatorRecords(resource, params) {
  const result = await request(getResourceUrl(resource), {
    method: 'GET',
    params: getQueryParams(resource, params),
  });
  return toProTableResult(result);
}

export async function queryLatestIpKernel(params) {
  const result = await request(getResourceUrl('ip-kernels'), {
    method: 'GET',
    params: getQueryParams('ip-kernels', {
      ...params,
      current: 1,
      pageSize: 1,
    }),
  });
  return result.data?.[0] || null;
}

export async function getIpCreatorRecord(resource, payload) {
  const data = getPayloadData(payload);
  return request(getResourceUrl(resource), {
    method: 'GET',
    params: {
      id: data.id,
      organizationId: data.organizationId,
    },
  });
}

export async function createIpCreatorRecord(resource, payload) {
  const data = getPayloadData(payload);
  return request(getResourceUrl(resource), {
    method: 'POST',
    params: {
      organizationId: data.organizationId,
    },
    data: getResourceData(resource, data),
  });
}

export async function updateIpCreatorRecord(resource, payload) {
  const data = getPayloadData(payload);
  const resourceData = getResourceData(resource, data);
  if (resource === 'article-arcs' && data.expectedUpdatedAt !== undefined) {
    resourceData.expectedUpdatedAt = data.expectedUpdatedAt;
  }
  return request(getResourceUrl(resource), {
    method: 'PUT',
    params: {
      id: data.id,
      organizationId: data.organizationId,
    },
    data: resourceData,
  });
}

export async function removeIpCreatorRecord(resource, payload) {
  const data = getPayloadData(payload);
  return request(getResourceUrl(resource), {
    method: 'DELETE',
    params: {
      id: data.id,
      organizationId: data.organizationId,
    },
  });
}

export async function createTopicLensFromHotPot(payload) {
  const data = getPayloadData(payload);
  return request('/api/agents/runs', {
    method: 'POST',
    params: {
      organizationId: data.organizationId,
    },
    data: {
      agentId: 'topic-lens',
      payload: { recordId: data.id, seedType: 'hotPot' },
    },
  });
}

export async function createFactAnchorFromHotPot(payload) {
  const data = getPayloadData(payload);
  return request('/api/agents/runs', {
    method: 'POST',
    params: {
      organizationId: data.organizationId,
    },
    data: {
      agentId: 'fact-anchor',
      payload: { recordId: data.id, seedType: 'hotPot' },
    },
  });
}

export async function createFactAnchorFromSeedPlan(payload) {
  const data = getPayloadData(payload);
  return request('/api/agents/runs', {
    method: 'POST',
    params: {
      organizationId: data.organizationId,
    },
    data: {
      agentId: 'fact-anchor',
      payload: { recordId: data.id, seedType: 'seedPlan' },
    },
  });
}

export async function createScriptArcFromTopicLens(payload) {
  const data = getPayloadData(payload);
  return request('/api/agents/runs', {
    method: 'POST',
    params: {
      organizationId: data.organizationId,
    },
    data: {
      agentId: 'script-arc',
      payload: { recordId: data.id },
    },
  });
}

export async function createArticleArcFromTopicLens(payload) {
  const data = getPayloadData(payload);
  return request('/api/agents/runs', {
    method: 'POST',
    params: {
      organizationId: data.organizationId,
    },
    data: {
      agentId: 'article-arc',
      payload: { recordId: data.id },
    },
  });
}
